import { z } from "zod";
import { client } from "./client";
import type { FilterOption } from "@/components/designSystem/FilterSelect";
import { csmsStatusSchema } from "@/pages/charger/type/chargerType";

/**
 * 충전기 커넥터 1건의 표시 정보.
 * 관리번호 조회 결과와 CS 상세의 `charger` 가 같은 모양이라 한 곳에서 정의한다.
 */
export const chargerConnectorInfoSchema = z.object({
  managed_id: z.string(),
  csms_charger_id: z.string(),
  connector_index: z.number(),
  charger_uuid: z.string(),
  connector_uuid: z.string(),
  vendor_name: z.string().nullish(),
  model_name: z.string().nullish(),
  rated_capacity: z.union([z.number(), z.string()]).nullish(),
  connector_shape: z.string().nullish(),
  // 화면에는 코드가 아니라 이 라벨을 쓴다. 라벨 미지정 코드는 코드값이 그대로 담겨 온다
  connector_shape_label: z.string().nullish(),
  csms_status: csmsStatusSchema.nullish(),
  status_updated_at: z.string().nullish(),
});
export type ChargerConnectorInfo = z.infer<typeof chargerConnectorInfoSchema>;

/** 관리번호 1건 조회 결과 (CS·유지보수 등록/수정 폼) */
export const chargerLookupSchema = chargerConnectorInfoSchema.extend({
  // 소속 충전소 — 충전기를 먼저 고른 경우 폼의 충전소를 자동으로 채우는 데 쓴다
  station_uuid: z.string().nullish(),
  station_name: z.string().nullish(),
});
export type ChargerLookup = z.infer<typeof chargerLookupSchema>;

/**
 * GET /charger 응답(커넥터 단위 row)을 위 플랫 스키마로 옮긴다.
 * - connector_uuid ← top-level uuid, charger_uuid/csms_charger_id ← charger.*
 * - vendor_name/model_name ← top-level charger_vendor_name/charger_model_name
 *   (charger.vendor_name/model_name 은 BE 미구현이라 목록 화면도 top-level 을 쓴다)
 */
const chargerConnectorRowSchema = z
  .object({
    uuid: z.string(),
    managed_id: z.string(),
    connector_index: z.number(),
    connector_shape: z.string(),
    connector_shape_label: z.string().nullish(),
    rated_capacity: z.union([z.number(), z.string()]),
    csms_status: csmsStatusSchema.nullish(),
    status_updated_at: z.string().nullish(),
    charger_vendor_name: z.string().nullish(),
    charger_model_name: z.string().nullish(),
    charger: z.object({
      uuid: z.string(),
      csms_charger_id: z.string(),
    }),
    // 롤아웃 순서 대비 nullish — 없으면 충전소 자동 입력만 동작하지 않는다
    station: z
      .object({ uuid: z.string(), name: z.string() })
      .nullish(),
  })
  .transform(
    ({
      uuid,
      charger,
      station,
      charger_vendor_name,
      charger_model_name,
      rated_capacity,
      ...rest
    }) => ({
      ...rest,
      connector_uuid: uuid,
      charger_uuid: charger.uuid,
      csms_charger_id: charger.csms_charger_id,
      station_uuid: station?.uuid,
      station_name: station?.name,
      vendor_name: charger_vendor_name,
      model_name: charger_model_name,
      rated_capacity:
        typeof rated_capacity === "string"
          ? parseFloat(rated_capacity)
          : rated_capacity,
    }),
  );

/** 관리번호로 충전기 커넥터 1건 조회 — 등록/수정 폼의 충전기 정보 패널용 */
export const getChargerByManagedId = async (
  managedId: string,
): Promise<ChargerLookup | null> => {
  const response = await client.get("/charger", {
    params: {
      search_type: "managed_id",
      keyword: managedId,
      offset: 0,
      limit: 1,
    },
  });
  const items = response.data.data?.items ?? [];
  if (items.length === 0) return null;

  const row = chargerConnectorRowSchema.safeParse(items[0]);
  if (!row.success) {
    console.warn(
      "Unexpected charger row shape for managed_id:",
      managedId,
      row.error,
    );
    return null;
  }
  return row.data;
};

/**
 * 관리번호 옵션 1건.
 * 소속 충전소를 함께 실어 보낸다 — 충전기를 먼저 고른 폼이 충전소를 채우려고
 * getChargerByManagedId 응답을 기다리지 않아도 되게 하기 위함이다.
 */
export interface ManagedIdOption extends FilterOption {
  stationUuid?: string;
}

const chargerNameInfoSchema = z.object({
  uuid: z.string(),
  csms_charger_id: z.string(),
  managed_id: z.string().nullable(),
});

const chargerByStationInfoSchema = z.object({
  // 고객사는 null — 내부 식별자다. 충전소 필터로 되돌려 보내는 값은 station_uuid 다
  station_id: z.number().nullish(),
  station_uuid: z.string(),
  station_name: z.string(),
  chargers: z.array(chargerNameInfoSchema),
});

export const chargerFilterOptionsSchema = z.array(chargerByStationInfoSchema);
export type ChargerFilterOptions = z.infer<typeof chargerFilterOptionsSchema>;

/** 옵션 조회 조건 — 상위 필터(운영사/충전소)를 그대로 내려보낸다 */
export interface ChargerOptionsScope {
  stationUuids?: string[] | null;
  cpoIds?: number[] | null;
}

/** 충전소별 충전기 옵션 트리. cpoIds 는 충전소의 운영사 기준으로 좁혀진다 */
export const getChargerFilterOptions = async ({
  stationUuids,
  cpoIds,
}: ChargerOptionsScope = {}): Promise<ChargerFilterOptions> => {
  const response = await client.get("/charger/filter-options", {
    params: {
      // 값이 없으면 파라미터 자체를 생략한다 (빈 배열은 보내지 않는다)
      ...(stationUuids?.length ? { station_uuids: stationUuids } : {}),
      ...(cpoIds?.length ? { cpo_ids: cpoIds } : {}),
    },
  });
  return chargerFilterOptionsSchema.parse(response.data.data);
};

/** 충전기 ID(csms_charger_id) 옵션. 상위 필터가 있으면 그 하위 충전기만 내려준다 */
export const getCsmsChargerIdOptions = async (
  scope: ChargerOptionsScope = {},
): Promise<FilterOption[]> => {
  const stations = await getChargerFilterOptions(scope);

  return stations
    .flatMap((station) =>
      station.chargers.map((charger) => ({
        value: charger.csms_charger_id,
        label: charger.csms_charger_id,
      })),
    )
    .sort((a, b) => a.value.localeCompare(b.value));
};

/** 충전기 관리번호 옵션. 상위 필터가 있으면 그 하위 충전기만 내려준다 */
export const getManagedIdOptions = async (
  scope: ChargerOptionsScope = {},
): Promise<ManagedIdOption[]> => {
  const stations = await getChargerFilterOptions(scope);

  return stations
    .flatMap((station) =>
      station.chargers.flatMap((charger) =>
        charger.managed_id
          ? [
              {
                value: charger.managed_id,
                label: charger.managed_id,
                stationUuid: station.station_uuid,
              },
            ]
          : [],
      ),
    )
    .sort((a, b) => a.value.localeCompare(b.value));
};
